import { Params } from '@angular/router';

import { RouterSelectors } from '@mitsubishi/core';
import { CurrencyUtils, DatasheetStatus, Unit } from '@mitsubishi/shared/data-access';

import { Selector, createSelector } from '@ngxs/store';

import { Selection, SelectionDetails, SelectionOffer, SelectionOrder, StatisticOption } from '../../models';
import { ProjectSelectors } from '../project/project.selectors';

export class ProjectSelectionsSelectors {
  @Selector([RouterSelectors.params])
  static getRouteSelectionId(params: Params) {
    return params.selectionId;
  }

  @Selector([RouterSelectors.params])
  static getActiveUnitId(params: Params) {
    return params.unitId;
  }

  @Selector([ProjectSelectionsSelectors.getRouteSelectionId, ProjectSelectors.projectSelections])
  static getActiveSelection(selectionId: string, selections: Selection[]): Selection | undefined {
    return selections.find(selection => selection.details.uuid === selectionId);
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionId(selection: Selection | undefined) {
    return selection ? selection.details.uuid : null;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionCurrencySymbol(selection: Selection | undefined) {
    return CurrencyUtils.getCurrencySymbolFromCode(selection?.details?.currencyCode ?? selection?.currencyCode);
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionCode(selection: Selection | undefined) {
    return selection ? selection.details.code : null;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionDetails(selection: Selection | undefined) {
    return selection?.details;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelectionDetails])
  static getActiveSelectionOrganizationId(selectionDetails: SelectionDetails | undefined) {
    return selectionDetails?.organizationId;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveQuoteOwnerId(selection: Selection | undefined) {
    return selection?.details.created.id;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionOffer(selection: Selection | undefined) {
    return selection ? selection.offer : undefined;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelectionOffer, ProjectSelectionsSelectors.getActiveSelection])
  static getActiveQuoteCustomCurrency(selectionOffer: SelectionOffer | undefined, selection: Selection | undefined) {
    return selectionOffer
      ? selectionOffer.customCurrency
      : CurrencyUtils.getCurrencySymbolFromCode(selection?.details?.currencyCode ?? selection?.currencyCode);
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelectionOffer])
  static isCustomCurrencyApplied(selectionOffer: SelectionOffer | undefined): boolean {
    return selectionOffer?.useCustomCurrency ?? false;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelectionOffer, ProjectSelectionsSelectors.getActiveSelection])
  static getActiveQuoteCostsCustomCurrency(
    selectionOffer: SelectionOffer | undefined,
    selection: Selection | undefined
  ) {
    if (!selectionOffer || !selectionOffer.useCustomCurrency) {
      return CurrencyUtils.getCurrencySymbolFromCode(selection?.details?.currencyCode ?? selection?.currencyCode);
    }
    return selectionOffer.customCurrency;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelectionOffer])
  static getActiveQuoteCustomCurrencyMultiplier(selectionOffer: SelectionOffer | undefined) {
    if (!selectionOffer) {
      return 1;
    }
    return selectionOffer.useCustomCurrency ? selectionOffer.currencyMultiplier : 1;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelectionOffer])
  static getActiveQuotePriceMultiplier(selectionOffer: SelectionOffer | undefined) {
    return selectionOffer?.priceMultiplier ?? null;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelectionOffer])
  static getActiveQuoteStatistics(selectionOffer: SelectionOffer | undefined): Record<string, StatisticOption> {
    return selectionOffer?.statisticData ?? {};
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveQuoteAlternatives(selection: Selection | undefined) {
    return Object.values(selection?.alternatives ?? {});
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionBaseAlternative(selection: Selection | undefined) {
    return selection ? selection.base : null;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveQuoteAllAlternatives(selection: Selection | undefined) {
    if (!selection) {
      return [];
    }
    return [selection.base, ...Object.values(selection.alternatives)];
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionDocuments(selection: Selection | undefined) {
    return Object.values(selection?.documents ?? {});
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionAllUnits(selection: Selection | undefined) {
    return Object.values(selection?.units ?? {});
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveSelectionUnits(selection: Selection | undefined) {
    return selection
      ? [...Object.values(selection.units), ...Object.values(selection.unitsCustom)].filter(unit => !unit.deleted)
      : [];
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getActiveQuoteDeletedUnits(selection: Selection | undefined) {
    return selection
      ? [...Object.values(selection.units), ...Object.values(selection.unitsCustom)].filter(unit => unit.deleted)
      : [];
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getOrderAlternative(selection: Selection | undefined) {
    if (selection) {
      const alternatives = [selection.base, ...Object.values(selection.alternatives)];
      return alternatives.find(alternative => alternative.uuid === selection.details.orderAlternative);
    }
    return null;
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection, ProjectSelectionsSelectors.getActiveUnitId])
  static getActiveUnit(selection: Selection | undefined, unitId: string) {
    return selection?.units[unitId];
  }

  static getActiveSelectionUnitById(unitId: string) {
    return createSelector([ProjectSelectionsSelectors.getActiveSelectionAllUnits], (units: Unit[]) => {
      return units.find(unit => unit.uuid === unitId);
    });
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection])
  static getAdditionalInfo(selection: Selection | undefined) {
    return Object.values(selection?.offer?.additionalInfos ?? {});
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelectionAllUnits])
  static areDatasheetsGenerating(units: Unit[]) {
    return units
      .filter(unit => !unit.deleted && !unit.isExternalUnit)
      .some(
        unit =>
          unit.hasPerformance && (unit.datasheetStatus == null || unit.datasheetStatus === DatasheetStatus.PROGRESS)
      );
  }
}
